import React, { useEffect, useRef, useMemo, useState } from 'react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Script } from 'molstar/lib/mol-script/script';
import { StructureSelection } from 'molstar/lib/mol-model/structure/query';
import { MolScriptBuilder as MS, MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { Color } from "molstar/lib/mol-util/color";
import { StructureElement, StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
import { CustomInteractions, InteractionsShape } from 'molstar/lib/extensions/interactions/transforms';
import { ShapeRepresentation3D } from 'molstar/lib/mol-plugin-state/transforms/representation';
import { StructureFocusRepresentation } from 'molstar/lib/mol-plugin/behavior/dynamic/selection/structure-focus-representation';
import { setSubtreeVisibility } from 'molstar/lib/mol-plugin/behavior/static/state';

// ──────────────────────────────────────────────────────────────────────────
// Contact extras: the full interaction network as dashed
// lines, and one label per contact residue. Both are built once per load and
// switched with `setSubtreeVisibility`, so the buttons never rebuild the scene.
// ──────────────────────────────────────────────────────────────────────────
const NETWORK_COLOR = 0x9AA5B1;     // muted grey-blue: the selected contact (khaki) stays readable
const NETWORK_RADIUS = 0.05;        // thinner than the selected contact (0.11)
const NETWORK_CARBON = 0x8C96A0;    // carbons of the network sticks; heteroatoms keep CPK
const CONTACT_LABEL_COLOR = 0x111111;

const toSeqId = (p) => {
    if (typeof p === 'number') return p;
    const m = String(p ?? '').match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : parseInt(String(p ?? ''), 10);
};

// Rows come straight from the contacts tables, which encode the pair in
// `details` as "CHAIN/POS/ATOM | CHAIN/POS/ATOM" — the same string the row-click
// handlers already parse. Explicit field names are accepted as a fallback, so a
// caller with a different row shape only has to touch this function.
function toNetworkPair(row) {
    if (!row) return null;

    if (typeof row.details === 'string' && row.details.includes('|')) {
        const [left, right] = row.details.split('|').map(part => part.trim().split('/'));
        if (left?.length >= 2 && right?.length >= 2) {
            const aPos = toSeqId(left[1]);
            const bPos = toSeqId(right[1]);
            if (!Number.isNaN(aPos) && !Number.isNaN(bPos)) {
                return {
                    aChain: left[0], aPos, aAtom: left[2],
                    bChain: right[0], bPos, bAtom: right[2],
                    distance: row.distance !== undefined ? parseFloat(row.distance) : undefined,
                };
            }
        }
    }
    const aPos = toSeqId(row.aPos ?? row.position1 ?? row.pos1 ?? row.res1);
    const bPos = toSeqId(row.bPos ?? row.position2 ?? row.pos2 ?? row.res2);
    if (Number.isNaN(aPos) || Number.isNaN(bPos)) return null;
    return {
        aChain: row.aChain ?? row.chain1 ?? row.chainA,
        aPos,
        aAtom: row.aAtom ?? row.atom1 ?? row.residue1,
        bChain: row.bChain ?? row.chain2 ?? row.chainB,
        bPos,
        bAtom: row.bAtom ?? row.atom2 ?? row.residue2,
        distance: row.distance !== undefined ? parseFloat(row.distance) : undefined,
    };
}

// Anchor resolution for the interaction lines. An endpoint whose atom doesn't
// exist in the residue (CB on a glycine, CA on a ligand) resolves to an empty
// loci, and an empty loci's bounding-sphere centre is the origin — that's what
// draws a dash off into empty space. Indexing the atom names once lets each
// endpoint fall back to a real atom, and lets pairs whose residue isn't in this
// structure be dropped instead of drawn.
function buildResidueAtomIndex(structureData) {
    const index = new Map();
    if (!structureData) return index;
    for (const unit of structureData.units) {
        if (!Unit.isAtomic(unit) || unit.elements.length === 0) continue;
        const loc = StructureElement.Location.create(structureData, unit, unit.elements[0]);
        for (let i = 0, il = unit.elements.length; i < il; i++) {
            loc.element = unit.elements[i];
            const key = `${StructureProperties.chain.auth_asym_id(loc)}|${StructureProperties.residue.auth_seq_id(loc)}`;
            let atoms = index.get(key);
            if (!atoms) { atoms = new Set(); index.set(key, atoms); }
            atoms.add(StructureProperties.atom.label_atom_id(loc));
        }
    }
    return index;
}

// -> { atom } with the atom to anchor on (undefined = whole residue, i.e. its
// centroid: the sane fallback for ligands and for GLY without CB), or null when
// the residue isn't present in this structure at all.
function resolveAnchorAtom(index, chainId, seqId, preferred) {
    const atoms = index.get(`${chainId}|${seqId}`);
    if (!atoms) return null;
    for (const candidate of [preferred, 'CB', 'CA']) {
        if (candidate && atoms.has(candidate)) return { atom: candidate };
    }
    return { atom: undefined };
}

async function addContactNetwork(plugin, structureRef, structureData, pairs, forcedChain, pdbId) {
    if (!structureRef || !pairs?.length) return [];

    // In residue mode the table can hold rows from several structures; only the
    // ones belonging to the structure on screen can resolve to a loci.
    const sameStructure = (row) => !row?.identifier || !pdbId
        || String(row.identifier).toUpperCase() === String(pdbId).toUpperCase();

    const atomIndex = buildResidueAtomIndex(structureData);
    const interactions = [];
    const residues = new Map();   // `chain|seq` -> { chain, seq }, for the sticks
    let dropped = 0;

    for (const row of pairs) {
        if (!sameStructure(row)) continue;
        const p = toNetworkPair(row);
        if (!p) continue;

        const aChain = forcedChain || p.aChain;
        const bChain = forcedChain || p.bChain;
        if (!aChain || !bChain) continue;

        const anchorA = resolveAnchorAtom(atomIndex, aChain, p.aPos, p.aAtom);
        const anchorB = resolveAnchorAtom(atomIndex, bChain, p.bPos, p.bAtom);
        if (!anchorA || !anchorB) { dropped++; continue; }

        interactions.push({
            kind: 'unknown',
            aStructureRef: structureRef,
            a: { auth_asym_id: aChain, auth_seq_id: p.aPos, ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}) },
            bStructureRef: structureRef,
            b: { auth_asym_id: bChain, auth_seq_id: p.bPos, ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}) },
            description: `${aChain}/${p.aPos} \u2194 ${bChain}/${p.bPos}`
                + (Number.isFinite(p.distance) ? ` - ${p.distance.toFixed(2)}\u00c5` : ''),
        });
        residues.set(`${aChain}|${p.aPos}`, { chain: aChain, seq: p.aPos });
        residues.set(`${bChain}|${p.bPos}`, { chain: bChain, seq: p.bPos });
    }

    if (dropped > 0) {
        console.warn(`Contact network: ${dropped} pair(s) skipped, residue not in ${pdbId}`);
    }
    if (!interactions.length) return [];

    const refs = [];
    try {
        const update = plugin.build();
        const node = update.toRoot().apply(
            CustomInteractions,
            { interactions },
            { dependsOn: [structureRef], label: 'Contact network' }
        );
        // `kinds` is left at its default: the param was renamed across Mol*
        // versions and the default already covers 'unknown'.
        node.apply(InteractionsShape, {
            styles: { unknown: { color: Color(NETWORK_COLOR), style: 'dashed', radius: NETWORK_RADIUS } },
        }).apply(ShapeRepresentation3D);
        await update.commit();
        refs.push(node.ref);
    } catch (e) {
        console.warn('Contact network failed:', e);
        return refs;
    }

    // Sticks for the residues the network touches, so the lines land on visible
    // side chains instead of bare cartoon (same treatment as the path).
    try {
        const structCell = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
        if (structCell && residues.size > 0) {
            const expressions = Array.from(residues.values()).map(({ chain, seq }) =>
                MolScriptBuilder.struct.generator.atomGroups({
                    'chain-test': MolScriptBuilder.core.rel.eq([
                        MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), chain,
                    ]),
                    'residue-test': MolScriptBuilder.core.rel.eq([
                        MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), seq,
                    ]),
                }));

            const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
                structCell,
                MolScriptBuilder.struct.combinator.merge(expressions),
                'contact-network-residues',
                { label: 'Contact network residues' }
            );
            if (sel) {
                await plugin.builders.structure.representation.addRepresentation(sel, {
                    type: 'ball-and-stick',
                    typeParams: { sizeFactor: 0.16, aspectRatio: 1, ignoreHydrogens: true },
                    color: 'element-symbol',
                    colorParams: {
                        carbonColor: { name: 'uniform', params: { value: Color(NETWORK_CARBON) } },
                        saturation: 0,
                        lightness: 0,
                    },
                });
                refs.push(sel.ref);
            }
        }
    } catch (e) {
        console.warn('Contact network sticks failed:', e);
    }

    return refs;
}

async function addContactLabels(plugin, positions, chains) {
    if (!positions?.length || !chains?.length) return null;
    const structRef = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
    if (!structRef) return null;

    // One atomGroup per (residue, chain) pair, merged: a position can never
    // bleed onto the wrong chain.
    const expressions = positions.map((pos, i) => {
        const seqId = toSeqId(pos);
        if (Number.isNaN(seqId) || !chains[i]) return null;
        return MolScriptBuilder.struct.generator.atomGroups({
            'chain-test': MolScriptBuilder.core.rel.eq([
                MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), chains[i],
            ]),
            'residue-test': MolScriptBuilder.core.rel.eq([
                MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), seqId,
            ]),
        });
    }).filter(Boolean);

    if (!expressions.length) return null;

    try {
        const combined = MolScriptBuilder.struct.combinator.merge(expressions);
        const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
            structRef, combined, 'contact-labels', { label: 'Contact labels' }
        );
        if (!sel) return null;
        const repr = await plugin.builders.structure.representation.addRepresentation(sel, {
            type: 'label',
            typeParams: {
                level: 'residue',
                attachment: 'middle-left',
                sizeFactor: 1.1,
                background: false,
                borderWidth: 0.2,
                borderColor: Color(0xFFFFFF),
                tether: false,
                offsetZ: 1.5,
            },
            color: 'uniform',
            colorParams: { value: Color(CONTACT_LABEL_COLOR) },
        });
        return repr?.ref ?? null;
    } catch (e) {
        console.warn('Contact labels failed:', e);
        return null;
    }
}

// Mol*'s viewport buttons sit in a 32px column inset by 10px from the top right
// (`.msp-viewport-controls`), so these sit to their left. Colours/metrics are
// the light skin's `.msp-form-control`, hardcoded because the skin compiles to
// plain CSS scoped under `.msp-plugin`.
const MSP_CONTROL_SPACING = 10;
const MSP_CONTROL_COLUMN = 32;
const MSP_BUTTON_BAR_STYLE = {
    position: 'absolute',
    top: MSP_CONTROL_SPACING,
    right: MSP_CONTROL_SPACING + MSP_CONTROL_COLUMN + 6,
    zIndex: 10,
    display: 'flex',
    gap: 6,
};
const MSP_BUTTON_STYLE = {
    height: 32,
    padding: '0 10px',
    border: 'none',
    borderRadius: 0,
    background: '#F3F2EE',
    color: '#332B1F',
    fontFamily: '"Helvetica Neue", "Segoe UI", Helvetica, "Source Sans Pro", Arial, sans-serif',
    fontSize: 14,
    lineHeight: '30px',
    boxShadow: 'none',
    cursor: 'pointer',
};

// The single selected contact keeps the khaki of the other viewers, drawn
// thicker than the network so it stays readable when both are on.
const SELECTED_CONTACT_COLOR = 0xF0E68C;
const SELECTED_CONTACT_RADIUS = 0.11;

const MolstarViewerCalculation = ({ positions, chain, cifString, pdbId, distance, residues, contactPairs }) => {

  const viewerRef = useRef(null);
  const pluginInstanceRef = useRef(null);

  // Contact extras: state refs of the network (shape + sticks) and label
  // subtrees. The *Ref mirrors let initMolstar honour the current toggle state
  // when the scene is rebuilt, without adding the toggles to its deps.
  const networkRefStore = useRef([]);
  const labelsRefStore = useRef(null);
  const [showNetwork, setShowNetwork] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [hovered, setHovered] = useState(null);
  const showNetworkRef = useRef(false);
  const showLabelsRef = useRef(true);

  const initMolstar = useMemo(() => async () => {
    if (viewerRef.current && !pluginInstanceRef.current) {
      const plugin = await createPluginUI({
        target: viewerRef.current,
        layoutIsExpanded: false,
        layoutShowControls: false,
        layoutShowRemoteState: false,
        layoutShowSequence: true,
        layoutShowLog: false,
        layoutShowLeftPanel: false,
        viewportShowExpand: false,
        viewportShowSelectionMode: false,
        viewportShowAnimation: false,
        render: renderReact18,
      });

      pluginInstanceRef.current = plugin;

      try {
        let data;
        data = await pluginInstanceRef.current.builders.data.rawData({
          data: cifString,
          label: pdbId ? `${pdbId}.cif` : 'structure.cif'
        });

          // --- MANUAL STRUCTURE CONSTRUCTION (Prevents Color Race Conditions) ---

        // 1. Parse Trajectory
        const trajectory = await pluginInstanceRef.current.builders.structure.parseTrajectory(data,  'mmcif');

        // 2. Create Model and Structure Nodes manually
        const model = await pluginInstanceRef.current.builders.structure.createModel(trajectory);
        const structureNode = await pluginInstanceRef.current.builders.structure.createStructure(model);

        // 3. Configure Background
        const renderer = plugin.canvas3d.props.renderer;
        PluginCommands.Canvas3D.SetSettings(plugin, { settings: { renderer: { ...renderer, backgroundColor: ColorNames.white } } });
        plugin.state.updateBehavior(StructureFocusRepresentation, { expandRadius: 8 });

        // 4. Create Components (Polymer and Ligands)
        const polymerComponent = await plugin.builders.structure.tryCreateComponentStatic(structureNode, 'polymer');
        const ligandComponent = await plugin.builders.structure.tryCreateComponentStatic(structureNode, 'ligand');

        // The ligand component had no representation, so a contact involving a
        // ligand zoomed onto empty space. Draw it in CPK ball-and-stick.
        if (ligandComponent) {
            await plugin.builders.structure.representation.addRepresentation(ligandComponent, {
                type: 'ball-and-stick',
                typeParams: { sizeFactor: 0.25, ignoreHydrogens: true },
                color: 'element-symbol',
            });
        }

        // 5. Add polymer representation with WHITE color immediately
        let polymerRep;
        if (polymerComponent) {
            polymerRep = await plugin.builders.structure.representation.addRepresentation(polymerComponent, {
                type: 'auto',
                color: 'uniform',
                colorParams: { value: Color(0xFAFAFA) } // Force White color at creation time!
            });
        }

        // Get structure data for selections
        const dta = structureNode.cell.obj?.data;
        if (!dta) return;

        // We define 'cartoon' as the polymer representation we just created
        const cartoon = polymerRep;


        // Set script language
        const language = 'mol-script';

        // Create params object
        const params = {
          layers: []
        };

        if (positions && positions.length > 0) {
            for (let i = 0; i < positions.length; i++) {
              params.layers.push({
                script: Script(
                  `(sel.atom.atom-groups
                                 :chain-test (= atom.auth_asym_id ${chain[i]} )
                                 :residue-test (set.has (set ${positions[i]}) atom.auth_seq_id)
                              )`,
                  language
                ),
                color: Color(0xf0e68c),
                clear: false
              });
            }
        }

        // Apply yellow color
        const update = plugin.build();
        update
          .to(cartoon)
          .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, params);

        await update.commit();

        if (distance  && residues && residues.length >= 2 && positions.length >= 2) {

             PluginCommands.Toast.Show(plugin, {
                 title: 'Contact',
                 message: `${chain[0]}/${positions[0]}/${residues[0]}  ${chain[1]}/${positions[1]}/${residues[1]} - ${distance}Å`
             });

             // ── Dashed line for the contact being zoomed into ──
             // `residues` carries the residue name, not necessarily an atom
             // name, and a ligand or modified residue may have neither CB nor
             // CA — resolveAnchorAtom falls back to the residue centroid so the
             // dash always lands on the residue instead of at the origin.
             const selAtomIndex = buildResidueAtomIndex(dta);
             const anchorA = resolveAnchorAtom(selAtomIndex, chain[0], toSeqId(positions[0]), residues[0]);
             const anchorB = resolveAnchorAtom(selAtomIndex, chain[1], toSeqId(positions[1]), residues[1]);

             if (structureNode?.ref && anchorA && anchorB) {
                 try {
                     const updateContact = plugin.build();
                     const contactNode = updateContact.toRoot().apply(
                         CustomInteractions,
                         {
                             interactions: [{
                                 kind: 'unknown',
                                 aStructureRef: structureNode.ref,
                                 a: {
                                     auth_asym_id: chain[0],
                                     auth_seq_id: toSeqId(positions[0]),
                                     ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}),
                                 },
                                 bStructureRef: structureNode.ref,
                                 b: {
                                     auth_asym_id: chain[1],
                                     auth_seq_id: toSeqId(positions[1]),
                                     ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}),
                                 },
                                 description: `${chain[0]}/${positions[0]}/${residues[0]} \u2194 ${chain[1]}/${positions[1]}/${residues[1]}`
                                     + (Number.isFinite(parseFloat(distance)) ? ` - ${parseFloat(distance).toFixed(2)}\u00c5` : ''),
                             }],
                         },
                         { dependsOn: [structureNode.ref], label: 'Selected contact' }
                     );
                     contactNode.apply(InteractionsShape, {
                         styles: {
                             unknown: {
                                 color: Color(SELECTED_CONTACT_COLOR),
                                 style: 'dashed',
                                 radius: SELECTED_CONTACT_RADIUS,
                             },
                         },
                     }).apply(ShapeRepresentation3D);
                     await updateContact.commit();
                 } catch (e) {
                     console.warn('Selected contact line failed:', e);
                 }
             } else {
                 console.warn('Selected contact line skipped, residue not in structure:',
                     `${chain[0]}/${positions[0]}`, `${chain[1]}/${positions[1]}`);
             }

             const selection = Script.getStructureSelection(Q => Q.struct.generator.atomGroups({
                "chain-test": Q.core.rel.eq([MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), ...chain]),
                'residue-test': Q.core.set.has([Q.set(...positions), Q.ammp('auth_seq_id')])
             }), dta);

              // Representation params for variants (as red spheres)
             const reprParams = createStructureRepresentationParams(plugin, undefined, {
                     type: 'ball-and-stick',
                     params: { sizeFactor: 0.2 } ,
                     color: 'element-symbol',
                     params: { carbonColor: { name: 'element-symbol' }},
                });

                const updateBall = plugin
                    .build()
                    .to(cartoon)
                    .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                        label: 'stick',
                        expression: selection
                    })
                    .apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);


              await updateBall.commit();

             const loci = StructureSelection.toLociWithSourceUnits(selection);
             plugin.managers.camera.focusLoci(loci);
        }

        // ── Contact extras: network (lines + sticks) and residue labels ──
        // Both are built once and switched with setSubtreeVisibility.
        networkRefStore.current = await addContactNetwork(
            plugin, structureNode?.ref, dta, contactPairs, undefined, pdbId);
        for (const ref of networkRefStore.current) {
            setSubtreeVisibility(plugin.state.data, ref, !showNetworkRef.current);
        }

        labelsRefStore.current = await addContactLabels(plugin, positions, chain);
        if (labelsRefStore.current) {
            setSubtreeVisibility(plugin.state.data, labelsRefStore.current, !showLabelsRef.current);
        }

      } catch (error) {
        console.error('Failed to load the PDB structure:', error);
      }
    }
  }, [positions, chain, cifString, pdbId, distance, residues, contactPairs]);


  useEffect(() => {
    initMolstar();

    return () => {
      if (pluginInstanceRef.current) {
        pluginInstanceRef.current.dispose();
        pluginInstanceRef.current = null;
      }
    };
  }, [initMolstar]);

  // Toggles: hide/show the existing subtrees, no rebuild.
  useEffect(() => {
    showNetworkRef.current = showNetwork;
    const plugin = pluginInstanceRef.current;
    if (!plugin || !networkRefStore.current?.length) return;
    for (const ref of networkRefStore.current) {
      setSubtreeVisibility(plugin.state.data, ref, !showNetwork);
    }
  }, [showNetwork]);

  useEffect(() => {
    showLabelsRef.current = showLabels;
    const plugin = pluginInstanceRef.current;
    if (!plugin || !labelsRefStore.current) return;
    setSubtreeVisibility(plugin.state.data, labelsRefStore.current, !showLabels);
  }, [showLabels]);

  const buttonStyle = (id) => ({
    ...MSP_BUTTON_STYLE,
    outline: hovered === id ? '1px solid #C9C4B2' : 'none',
    outlineOffset: -1,
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: '400px' }}>
      <div key="calculation" ref={viewerRef} style={{ width: '100%', height: '100%' }} />
      <div style={MSP_BUTTON_BAR_STYLE}>
        <button
          type="button"
          onClick={() => setShowNetwork(v => !v)}
          onMouseEnter={() => setHovered('network')}
          onMouseLeave={() => setHovered(null)}
          title="All computed contacts as dashed lines"
          disabled={!contactPairs?.length}
          style={{
            ...buttonStyle('network'),
            opacity: contactPairs?.length ? 1 : 0.5,
            cursor: contactPairs?.length ? 'pointer' : 'default',
          }}
        >
          {showNetwork ? 'Hide network' : 'Show network'}
        </button>
        <button
          type="button"
          onClick={() => setShowLabels(v => !v)}
          onMouseEnter={() => setHovered('labels')}
          onMouseLeave={() => setHovered(null)}
          title="Residue labels on the contact residues"
          style={buttonStyle('labels')}
        >
          {showLabels ? 'Hide labels' : 'Show labels'}
        </button>
      </div>
    </div>
  );
};

export default MolstarViewerCalculation;